import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    type UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { HTMLAttributes, ReactNode } from 'react'
import { useMemo } from 'react'

export type DragAndDropListDragHandleProps = HTMLAttributes<HTMLElement> & {
    ref: (element: HTMLElement | null) => void
}

export type DragAndDropListRenderItemProps<TItem> = {
    item: TItem
    index: number
    isDragging: boolean
    dragHandleProps: DragAndDropListDragHandleProps
}

export type DragAndDropListProps<TItem> = {
    items: TItem[]
    getItemId: (item: TItem) => UniqueIdentifier
    onReorder: (items: TItem[]) => void
    renderItem: (props: DragAndDropListRenderItemProps<TItem>) => ReactNode
    disabled?: boolean
    gap?: number
    sx?: SxProps<Theme>
}

const SortableListItem = <TItem,>({
    item,
    itemId,
    index,
    disabled,
    renderItem,
}: {
    item: TItem
    itemId: UniqueIdentifier
    index: number
    disabled?: boolean
    renderItem: (props: DragAndDropListRenderItemProps<TItem>) => ReactNode
}) => {
    const {
        attributes,
        isDragging,
        listeners,
        setActivatorNodeRef,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: itemId, disabled })
    const dragHandleProps = {
        ref: setActivatorNodeRef,
        ...attributes,
        ...(listeners ?? {}),
    } as DragAndDropListDragHandleProps

    return (
        <Box
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
            }}
        >
            {renderItem({
                item,
                index,
                isDragging,
                dragHandleProps,
            })}
        </Box>
    )
}

const DragAndDropList = <TItem,>({
    items,
    getItemId,
    onReorder,
    renderItem,
    disabled,
    gap = 0,
    sx,
}: DragAndDropListProps<TItem>) => {
    const listSx = {
        display: 'flex',
        flexDirection: 'column',
        gap,
    }
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    )
    const itemIds = useMemo(() => items.map(getItemId), [getItemId, items])

    const handleDragEnd = (event: DragEndEvent): void => {
        const { active, over } = event

        if (!over || active.id === over.id) {
            return
        }

        const oldIndex = itemIds.indexOf(active.id)
        const newIndex = itemIds.indexOf(over.id)

        if (oldIndex === -1 || newIndex === -1) {
            return
        }

        onReorder(arrayMove(items, oldIndex, newIndex))
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
        >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <Box sx={sx ? [listSx, ...(Array.isArray(sx) ? sx : [sx])] : listSx}>
                    {items.map((item, index) => {
                        const itemId = itemIds[index]

                        return (
                            <SortableListItem
                                key={itemId}
                                item={item}
                                itemId={itemId}
                                index={index}
                                disabled={disabled}
                                renderItem={renderItem}
                            />
                        )
                    })}
                </Box>
            </SortableContext>
        </DndContext>
    )
}

export default DragAndDropList
